import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { APP_NAME } from '../../constants/app';
import { useNavigate } from 'react-router-dom';
import NotificationCenter from '../../components/notifications/NotificationCenter';
import { useViewport } from '../../contexts/ViewportContext';
import Tooltip from '../shared/Tooltip';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';
import { logger } from '../../utils/logger';
import { useTopicUIStore } from '../../store/topicUIStore';

interface SidebarHeaderProps {
  onCollapse?: (() => void) | undefined;
  children?: React.ReactNode;
  showShadow?: boolean;
}

export function SidebarHeader({ onCollapse, children, showShadow = false }: SidebarHeaderProps) {
  const navigate = useNavigate();
  const { setActiveTopicId } = useTopicUIStore();
  const { isMobile } = useViewport();

  // Get status bar information for mobile adjustments
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // Log status bar info for debugging
  React.useEffect(() => {
    if (isNativeApp && isMobile) {
      logger.capacitor('SidebarHeader: Using status bar height', {
        statusBarHeight,
      });
    }
  }, [isNativeApp, statusBarHeight, isMobile]);

  const handleDashboardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Reset active topic before navigating to dashboard
    setActiveTopicId(null);
    navigate('/dashboard');
  };

  return (
    <div
      className={`py-4 px-3 SidebarHeader ${isMobile ? 'sticky top-0 z-50' : ''} bg-[var(--sidebar-bg-color)] transition-shadow duration-200 ${
        isMobile && showShadow ? 'shadow-md' : ''
      }`}
      style={{
        paddingTop: isNativeApp && isMobile ? `${statusBarHeight + 16}px` : '1rem',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Logo and controls in a single flex container with full width */}
      <div className="flex items-center justify-between w-full">
        {/* Left: Logo or custom children */}
        <div className="flex-shrink-0">
          {children || (
            <button
              onClick={handleDashboardClick}
              className="transition-all duration-200 flex items-center gap-2 group relative"
            >
              <img
                src="/logo-icon-white.svg"
                alt="Zentime Logo"
                className="h-10 w-auto transition-all duration-200"
                style={{ transform: 'translate(-2px, 1px)' }}
              />
              <div className="relative">
                <h1
                  className="text-xl font-bold font-['Sora']"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {APP_NAME}
                </h1>
              </div>
            </button>
          )}
        </div>

        {/* Right: Controls with flexbox */}
        <div className="flex items-center space-x-3">
          {/* Notification Center */}
          <NotificationCenter />

          {/* Collapse Button */}
          {onCollapse && (
            <Tooltip content="Fold sidebar (keyboard shortcut A or P)" position="bottom">
              <button
                onClick={onCollapse}
                className="hidden lg:block p-1.5 mt-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}